import {create} from 'zustand'
import {TCompanyId} from "@/common/types";
import {
    IProtocolDefinition,
    IProtocolStepDefinition,
    TProtocolDefinitionId,
    TProtocolStepDefinitionId,
} from "../types/protocolDefinitions";
import * as protocolsService from "../services/protocols";
import {showErrorNotification} from "../../common/notifications";
import {deepMerge} from "@/utils/merging.ts";
import {DeepPartial} from "@/utils/types/DeepPartial";
import {SupportedStimDevices, TStimDeviceKind} from "@/common/types/StimDevice.ts";
import {IProtocolStepKind} from "@/protocols/types/stepKind.ts";
import {IStimStep} from "@/protocols/types/protocol.ts";

/**
 * Represents the state of protocols feature set within the application.
 *
 * This type defines the structure of the `ProtocolsState` object,
 * which includes information about the current active protocols,
 * their loading statuses, and whether a new protocol is being created.
 *
 * Properties:
 *
 * - `protocols`: An array of all protocol objects of type `IProtocol`.
 * - `protocolsLoading`: A boolean indicating whether the protocols are currently being loaded.
 * - `creatingProtocol`: A boolean indicating whether a new protocol is in the process of being created.
 */
export type ProtocolDefinitionsState = {
    protocolDefinitions: Array<IProtocolDefinition>
    protocolDefinitionsLoading: boolean
    creatingProtocolDefinition: boolean
    savingProtocolDefinition: boolean
    protocolStepDefinitionsLoading: boolean
}
export type ProtocolDefinitionsActions = {
    reset: () => void
    loadProtocolDefinition: (companyId: TCompanyId, pDefId: TProtocolDefinitionId, steps: boolean) => Promise<IProtocolDefinition>
    loadProtocolDefinitions: (companyId: TCompanyId) => Promise<Array<IProtocolDefinition>>
    loadProtocolDefinitionSteps: (companyId: TCompanyId, protocol: IProtocolDefinition) => Promise<IProtocolDefinition>;
    addProtocolDefinition: (companyId: TCompanyId, protocol: IProtocolDefinition) => void
    updateProtocolDefinition: (companyId: TCompanyId, protocol: IProtocolDefinition) => void
    deleteProtocolDefinition: (companyId: TCompanyId, id: TProtocolDefinitionId) => void
    insertStepDefinition: (companyId: TCompanyId, protocol: TProtocolDefinitionId, step: IProtocolStepDefinition, index: number) => void
    deleteStepDefinition: (companyId: TCompanyId, protocol: TProtocolDefinitionId, step: IProtocolStepDefinition) => void
    /**
     * Update a step definition with partial updates. Uses deepMerge to combine
     * the partial updates with the existing step.
     * @param companyId - Company ID
     * @param protocolId - Protocol Definition ID
     * @param stepId - Step Definition ID to update
     * @param stepUpdates - Partial updates to apply to the step
     * @returns The merged step definition, or undefined if step was not found
     */
    updateStepDefinition: (companyId: TCompanyId, protocolId: TProtocolDefinitionId, stepId: TProtocolStepDefinitionId, stepUpdates: DeepPartial<IProtocolStepDefinition>) => IProtocolStepDefinition | undefined
}

export const useProtocolDefinitionsStore = create<ProtocolDefinitionsState & ProtocolDefinitionsActions>((set, get, store) => {
    const getProtocol = (state: ProtocolDefinitionsState & ProtocolDefinitionsActions, id: TProtocolDefinitionId): IProtocolDefinition => {
        const protocol = state.protocolDefinitions.find((p) => p.id === id)
        if (!protocol)
            throw new Error(`Protocol Definition ${id} not found`)
        return protocol
    }
    const updateProtocol = (state: ProtocolDefinitionsState & ProtocolDefinitionsActions, protocol: IProtocolDefinition): Partial<ProtocolDefinitionsState & ProtocolDefinitionsActions> => {
        let index = state.protocolDefinitions.findIndex((p) => p.id === protocol.id)
        if (index >= 0) {
            const newProtocols = [...state.protocolDefinitions]
            newProtocols[index] = protocol
            return {protocolDefinitions: newProtocols}
        } else {
            console.error(`Cannot find protocol with id:${protocol.id}`)
            return {}
        }
    }

    return ({
        protocolDefinitions: [],
        protocolDefinitionsLoading: false,
        creatingProtocolDefinition: false,
        protocolStepDefinitionsLoading: false,
        savingProtocolDefinition: false,
        reset: () => {
            set(() => store.getInitialState())
        },
        loadProtocolDefinition: async (companyId: TCompanyId, pDefId: TProtocolDefinitionId, steps: boolean = true) => {
            set(() => ({protocolDefinitionsLoading: true}))
            const protocol = await protocolsService.loadProtocolDefinition(companyId, pDefId, steps).finally(() => {
                set((state) => ({protocolDefinitionsLoading: false}))
            })
            set((state) => {
                const index = state.protocolDefinitions.findIndex((p) => p.id === protocol.id)
                if (index >= 0) {
                    return updateProtocol(state, protocol)
                } else {
                    return {protocolDefinitions: [...state.protocolDefinitions, protocol]}
                }
            })
            return protocol
        },
        loadProtocolDefinitions: async (companyId: TCompanyId) => {
            set(() => ({protocolDefinitionsLoading: true}))
            try {
                const protocols = await protocolsService.loadProtocolDefinitions(companyId)
                set((state) => ({protocolDefinitions: protocols, protocolDefinitionsLoading: false}))
                return protocols
            } catch (error) {
                set((state) => ({protocolDefinitionsLoading: false}))
                return []
            }
        },
        loadProtocolDefinitionSteps: async (companyId: TCompanyId, protocol: IProtocolDefinition) => {
            set(() => ({protocolStepDefinitionsLoading: true}))
            try {
                const steps = await protocolsService.loadProtocolDefinitionSteps(companyId, protocol.id)
                set((state) => ({protocolStepDefinitionsLoading: false}))
                set((state) => updateProtocol(state, {...protocol, steps}))
                return protocol
            } catch (error) {
                set((state) => ({protocolDefinitionsLoading: false}))
                return protocol
            }
        },
        addProtocolDefinition: (companyId: TCompanyId, protocol) => {
            set((state) => ({protocolDefinitions: [protocol, ...state.protocolDefinitions]}))
        },
        updateProtocolDefinition: async (companyId: TCompanyId, protocol: IProtocolDefinition) => {
            set((state) => updateProtocol(state, protocol))
        },
        deleteProtocolDefinition: (companyId: TCompanyId, id: TProtocolDefinitionId) => {
            set((state) => {
                const index = state.protocolDefinitions?.findIndex((p) => p.id === id)
                if (index < 0) {
                    console.log(`Failed to find protocol id:${id}`)
                    return {}
                }
                const newProtocols = [...state.protocolDefinitions]
                newProtocols.splice(index, 1)
                return {...state, protocolDefinitions: newProtocols}
            })
        },
        insertStepDefinition: (companyId: TCompanyId, protocolId: TProtocolDefinitionId, step: IProtocolStepDefinition, index: number) => {
            set((state) => {
                const protocol = getProtocol(state, protocolId)
                const newSteps = [...protocol.steps.slice(0, index), step, ...protocol.steps.slice(index)]
                const updatedProtocol = {...protocol, steps: newSteps}
                return updateProtocol(state, updatedProtocol)
            })
        },
        deleteStepDefinition: (companyId: TCompanyId, protocolId: TProtocolDefinitionId, step: IProtocolStepDefinition) => {
            set((state) => {
                    const protocol = getProtocol(state, protocolId)
                    const newSteps = [...protocol.steps]
                    const index = protocol.steps.findIndex((st) => st.id === step.id)
                    newSteps.splice(index, 1)
                    const updatedProtocol = {...protocol, steps: newSteps}
                    if (index >= 0) {
                        return updateProtocol(state, updatedProtocol)
                    } else {
                        console.error(`Cannot find ${step.id} in ${protocol.id}`)
                        return {}
                    }
                }
            )
        },
        updateStepDefinition: (_companyId: TCompanyId, protocolId: TProtocolDefinitionId, stepId: TProtocolStepDefinitionId, stepUpdates: DeepPartial<IProtocolStepDefinition>): IProtocolStepDefinition | undefined => {
            let mergedStep: IProtocolStepDefinition | undefined;
            set((state) => {
                const protocol = getProtocol(state, protocolId)
                const index = protocol.steps.findIndex((st) => st.id === stepId)
                if (index < 0) {
                    showErrorNotification({
                        title: "Error",
                        message: `Cannot find step.id:${stepId} in protocol.id:${protocol.id}`
                    })
                    // no updates on error
                    return {}
                }

                const existingStep = protocol.steps[index]
                if (existingStep.kind === IProtocolStepKind.STIM_EVENT) {
                    const newStepUpdates = stepUpdates as DeepPartial<IStimStep>
                    const existingDeviceKind = existingStep.stimMetadata?.stimDevice?.kind

                    const newDeviceKind = newStepUpdates.stimMetadata?.stimDevice?.kind

                    if (newDeviceKind && newDeviceKind !== existingDeviceKind) {
                        const newStimMeta = newStepUpdates.stimMetadata || {}
                        if (newDeviceKind === TStimDeviceKind.BscPrograms) {
                            newStimMeta.config = undefined
                            newStimMeta.program_name = undefined
                            newStimMeta.stimDevice = SupportedStimDevices[1]
                        } else if (newDeviceKind === TStimDeviceKind.NeuroTracSports) {
                            newStimMeta.program_name = undefined
                            newStimMeta.config = undefined
                            newStimMeta.stimDevice = SupportedStimDevices[0]
                        }
                        newStepUpdates.stimMetadata = newStimMeta
                    }
                }

                // Use deepMerge to combine existing step with partial updates
                mergedStep = deepMerge(existingStep, stepUpdates) as IProtocolStepDefinition

                const newSteps = [...protocol.steps]
                newSteps[index] = mergedStep
                const updatedProtocol = {...protocol, steps: newSteps}
                return updateProtocol(state, updatedProtocol)
            })
            return mergedStep
        },
    })
})
